import React, { useEffect, useState } from 'react'
import * as api from '../../api'
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FabFrIndicator from '../../Plateforme/Atoms/FabFrIndicator';
import { getEventDesignerDisplayName, getEventDesignerProfileLink } from './utils/eventDesignerHelpers';
import BoldBlue from '../../Plateforme/Atoms/BoldBlue';
import H2 from '../../Plateforme/Atoms/H2';
import { supabase } from '../../lib/supabaseClient';

const Container = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
`

const Header = styled.div`
    text-align: center;
    margin-bottom: 30px;

    h2 {
        color: #1a1a2e;
        margin-bottom: 8px;
    }

    .subtitle {
        color: #666;
        font-size: 14px;
        max-width: 500px;
        margin: 0 auto;
    }
`

const ProjectThumbnail = styled.div`
    width: 70px;
    height: 70px;
    border-radius: 4px;
    overflow: hidden;
    flex-shrink: 0;
    margin-right: 14px;
    background: #f0f0f0;
    border: 1px solid #e0e0e0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`

const RatingLine = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 18px;
    border-radius: 4px;
    margin-bottom: 10px;
    background: white;
    border: 1px solid #e0e0e0;
    transition: all 0.2s ease;

    &:hover {
        border-color: #0000ff;
    }

    &.voted {
        background: #f0f4ff;
        border-color: #0000ff;
    }

    > .project-content {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
    }

    > .project-content > .projinfo {
        flex: 1;
        min-width: 0;

        a {
            color: #1a1a2e;
            text-decoration: none;
            cursor: pointer;
            transition: color 0.2s;

            &:hover {
                color: #0000ff;
            }
        }

        .project-name {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 4px;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .designer-name {
            font-size: 14px;
            color: #666;
        }
    }

    > .stars {
        display: flex;
        align-items: center;
        gap: 6px;
        margin: 0 16px;

        > span {
            cursor: pointer;
            color: #0000ff;
            font-size: 24px;
            transition: transform 0.15s;

            &:hover {
                transform: scale(1.1);
            }
        }
    }

    > label {
        display: flex;
        align-items: center;
        margin: 0 12px;
        cursor: pointer;
        padding: 6px 12px;
        border-radius: 4px;
        background: #f5f5f5;
        transition: all 0.2s;

        &:hover {
            background: #e8e8e8;
        }

        > span {
            margin-right: 8px;
            font-size: 13px;
            color: #333;
        }

        > input {
            width: 16px;
            height: 16px;
            cursor: pointer;
        }
    }

    > .comment-input {
        flex: 0 0 280px;
        padding: 8px 12px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 13px;
        transition: border-color 0.2s;

        &:focus {
            outline: none;
            border-color: #0000ff;
        }

        &::placeholder {
            color: #999;
        }
    }
`

const StatsContainer = styled.div`
    padding: 16px 20px;
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 30px;
    color: #333;
    border-bottom: 1px solid #e0e0e0;

    .stats-item {
        display: flex;
        align-items: baseline;
        gap: 6px;

        .number {
            font-size: 1.4em;
            font-weight: 600;
            color: #0000ff;
        }

        .label {
            font-size: 13px;
            color: #666;
        }
    }

    .progress-container {
        flex: 1;
        max-width: 300px;

        .progress-bar {
            width: 100%;
            height: 8px;
            background: #e0e0e0;
            border-radius: 4px;
            overflow: hidden;

            .progress-fill {
                height: 100%;
                background: #0000ff;
                transition: width 0.5s ease;
            }
        }

        .progress-label {
            margin-top: 4px;
            font-size: 12px;
            color: #666;
        }
    }
`

const SaveNotification = styled.div`
    position: fixed;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
    background: ${props => props.status === 'loading' ? '#0000cc' : props.status === 'saved' ? '#28a745' : '#dc3545'};
    color: white;
    padding: 14px 28px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    transition: all 0.3s ease;
    opacity: ${props => props.show ? 1 : 0};
    transform: translateX(-50%) translateY(${props => props.show ? '0' : '20px'});
    pointer-events: ${props => props.show ? 'auto' : 'none'};

    .spinner {
        margin-right: 10px;
        width: 18px;
        height: 18px;
        border: 2px solid rgba(255,255,255,0.3);
        border-top: 2px solid white;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    .checkmark {
        margin-right: 8px;
        font-size: 18px;
    }
`

const FilterContainer = styled.div`
    background: white;
    padding: 16px 20px;
    border-radius: 4px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    border: 1px solid #e0e0e0;

    .filter-group {
        display: flex;
        align-items: center;
        gap: 10px;

        .filter-label {
            font-weight: 500;
            color: #333;
            font-size: 14px;
        }

        .star-filter {
            display: flex;
            gap: 6px;

            button {
                border: 1px solid #0000ff;
                background: white;
                color: #0000ff;
                padding: 6px 14px;
                border-radius: 4px;
                cursor: pointer;
                font-size: 13px;
                font-weight: 500;
                transition: all 0.2s ease;

                &.active {
                    background: #0000ff;
                    color: white;
                }

                &:hover:not(.active) {
                    background: #0000ee;
                    color: white;
                }
            }
        }

        .search-input {
            padding: 8px 14px;
            border: 1px solid #ccc;
            border-radius: 4px;
            width: 260px;
            font-size: 14px;
            transition: all 0.2s;

            &:focus {
                outline: none;
                border-color: #0000ff;
                box-shadow: 0 0 0 2px rgba(0, 0, 255, 0.1);
            }
        }
    }
`

const ProjectsSection = styled.div`
    background: #f8f9fa;
    padding: 20px;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
`

const EVENT_ID = "67590da5499bcc995c210b40"; // FD100 2025 event ID

// Contract mention projects - These are the 6 "Contract" projects with French fabrication
const CONTRACT_PROJECT_IDS = [
    '680fa844a71ddf126b77b977', // CEYSSON & BENETIÈRE GALLERY - Sophie Dries
    '67e57f5143d5609350e3519a', // VILLA - MHNA
    '680113739167c049fff52d73', // FRANCE PAVILLION – EXPO 2025 OSAKA - JOSE LEVY
    '68025d069167c049fff56132', // OFFSIDE BAR - EL'LE
    '67e51ddb43d5609350e33667', // THE ÉCOLE DES ARTS JOAILLIERS - CONSTANCE GUISSET
    '67da910a43d5609350e274d7', // ORIENT EXPRESS CORINTHIAN - ANGEAC
];

const MentionContractJuryProfile = (props) => {

    const { i18n } = useTranslation('common');
    const [event, setEvent] = React.useState({});
    const [allProjects, setAllProjects] = React.useState([]);
    const [ratings, setRatings] = React.useState([]);
    const [dataStatus, setDataStatus] = React.useState("loading");
    const [starFilter, setStarFilter] = React.useState("all");
    const [searchFilter, setSearchFilter] = React.useState("");
    const [showSaveNotification, setShowSaveNotification] = React.useState(false);
    const [editedDataMap, setEditedDataMap] = useState({});

    // Get event
    useEffect(() => {
        if (props.user.eventId) {
            api.getEventById({ id: props.user.eventId }).then((response) => {
                console.log(response.data);
                setEvent(response.data);
            }).catch((error) => {
                console.log(error);
            });
        }
    }, [props.user.eventId])

    // Get contract projects from FD100 2025 winners
    useEffect(() => {
        const fetchProjectData = async () => {
            try {
                // Fetch all participating users with their projects
                const response = await api.getParticipatingUsersWithData({ eventId: EVENT_ID });

                let _allProjects = [];
                response.data.forEach(user => {
                    if (user.participation.data.projets) {
                        user.participation.data.projets.forEach(project_id => {
                            const project = user.company.projects.find((projet) => projet._id === project_id);
                            // Get portfolio project which has file data (files1, files2, files3)
                            const portfolioProject = user.portfolio?.find((p) => p._id === project_id);
                            // Only include validated projects that are in the CONTRACT_PROJECT_IDS list
                            if (project && project.status === "validé" && CONTRACT_PROJECT_IDS.includes(project._id)) {
                                _allProjects.push({
                                    projet: {
                                        ...project,
                                        participationId: user.participation._id,
                                        // Merge file data from portfolio if available
                                        files1: portfolioProject?.files1 || project.files1 || [],
                                        files2: portfolioProject?.files2 || project.files2 || [],
                                        files3: portfolioProject?.files3 || project.files3 || []
                                    },
                                    user: user
                                });
                            }
                        });
                    }
                });

                console.log("Contract mention projects:");
                console.log(_allProjects);
                // Debug: log medias data for first project
                if (_allProjects.length > 0) {
                    console.log("First project medias:", _allProjects[0].projet.medias);
                    console.log("First project full data:", _allProjects[0].projet);
                }
                setAllProjects(_allProjects);
            } catch (error) {
                console.error('Error fetching project data:', error);
            } finally {
                setDataStatus("saved");
            }
        };

        fetchProjectData();
    }, [])

    // Fetch edited data from Supabase for display consistency
    useEffect(() => {
        if (allProjects.length === 0) return;

        const fetchEditedData = async () => {
            const projectIds = allProjects.map(p => p.projet._id);

            try {
                const { data: trackingData, error } = await supabase
                    .from('fd100_laureats_tracking')
                    .select('project_id, edited_project_name, edited_creator_name')
                    .in('project_id', projectIds);

                if (!error && trackingData) {
                    const dataMap = {};
                    trackingData.forEach(record => {
                        dataMap[record.project_id] = {
                            projectName: record.edited_project_name,
                            creatorName: record.edited_creator_name
                        };
                    });
                    console.log('Loaded edited data from Supabase:', dataMap);
                    setEditedDataMap(dataMap);
                }
            } catch (err) {
                console.error('Error fetching edited data from Supabase:', err);
            }
        };

        fetchEditedData();
    }, [allProjects]);

    // Helper to get display name (edited or original)
    const getDisplayProjectName = (project) => {
        const edits = editedDataMap[project.projet._id];
        return edits?.projectName || project.projet.name_en || project.projet.name;
    };

    // Helper to get display creator name (edited or original)
    const getDisplayCreatorName = (project) => {
        const edits = editedDataMap[project.projet._id];
        return edits?.creatorName || getEventDesignerDisplayName(project.projet, project.user);
    };

    // Load jury's ratings from Supabase
    useEffect(() => {
        if (!props.user?._id) return;

        const loadRatings = async () => {
            try {
                const { data, error } = await supabase
                    .from('fd100_mention_contract_votes')
                    .select('*')
                    .eq('user_id', props.user._id);

                if (error) {
                    console.error('Error loading ratings from Supabase:', error);
                    return;
                }

                // Convert Supabase format to local format
                const formattedRatings = (data || []).map(vote => ({
                    participationId: vote.participation_id,
                    projetId: vote.project_id,
                    stars: vote.stars || 0,
                    Coup_de_coeur: vote.coup_de_coeur ? "checked" : "",
                    commentaire: vote.commentaire || ""
                }));

                console.log('Loaded ratings from Supabase:', formattedRatings);
                setRatings(formattedRatings);
            } catch (err) {
                console.error('Error loading ratings:', err);
            }
        };

        loadRatings();
    }, [props.user?._id]);

    // Save a single vote to Supabase
    const saveVoteToSupabase = async (projectId, participationId, voteData) => {
        setDataStatus("loading");
        setShowSaveNotification(true);

        try {
            const { error } = await supabase
                .from('fd100_mention_contract_votes')
                .upsert({
                    user_id: props.user._id,
                    user_email: props.user.email,
                    user_name: `${props.user.first_name || ''} ${props.user.last_name || ''}`.trim(),
                    project_id: projectId,
                    participation_id: participationId,
                    stars: voteData.stars || 0,
                    coup_de_coeur: voteData.Coup_de_coeur === "checked",
                    commentaire: voteData.commentaire || ""
                }, {
                    onConflict: 'user_id,project_id'
                });

            if (error) {
                console.error('Error saving vote to Supabase:', error);
                setDataStatus("error");
            } else {
                console.log('Vote saved to Supabase');
                setDataStatus("saved");
            }
        } catch (err) {
            console.error('Error saving vote:', err);
            setDataStatus("error");
        }

        setTimeout(() => {
            setShowSaveNotification(false);
        }, 2000);
    };

    async function setStar(projet, starNumber) {
        let _ratings = [...ratings];
        let _ratingIndex = _ratings.findIndex(rating => rating.projetId === projet.projet._id && rating.participationId === projet.projet.participationId);

        let voteData;
        if (_ratingIndex !== -1) {
            _ratings[_ratingIndex].stars = starNumber;
            voteData = _ratings[_ratingIndex];
        } else {
            voteData = { participationId: projet.projet.participationId, projetId: projet.projet._id, stars: starNumber };
            _ratings.push(voteData);
        }
        setRatings(_ratings);

        // Save to Supabase immediately
        await saveVoteToSupabase(projet.projet._id, projet.projet.participationId, voteData);
    }

    async function setCheckbox(projet, e) {
        let _ratings = [...ratings];
        let _ratingIndex = _ratings.findIndex(rating => rating.projetId === projet.projet._id && rating.participationId === projet.projet.participationId);

        let voteData;
        if (_ratingIndex !== -1) {
            _ratings[_ratingIndex][e.target.name] = e.target.checked ? "checked" : "";
            voteData = _ratings[_ratingIndex];
        } else {
            voteData = { participationId: projet.projet.participationId, projetId: projet.projet._id, [e.target.name]: e.target.checked ? "checked" : "" };
            _ratings.push(voteData);
        }
        setRatings(_ratings);

        // Save to Supabase immediately
        await saveVoteToSupabase(projet.projet._id, projet.projet.participationId, voteData);
    }

    async function setCommentaire(projet, e) {
        let _ratings = [...ratings];
        let _ratingIndex = _ratings.findIndex(rating => rating.projetId === projet.projet._id && rating.participationId === projet.projet.participationId);

        let voteData;
        if (_ratingIndex !== -1) {
            _ratings[_ratingIndex].commentaire = e.target.value;
            voteData = _ratings[_ratingIndex];
        } else {
            voteData = { participationId: projet.projet.participationId, projetId: projet.projet._id, commentaire: e.target.value };
            _ratings.push(voteData);
        }
        setRatings(_ratings);

        // Debounce comment saves (only save after user stops typing)
        if (window.commentSaveTimeout) {
            clearTimeout(window.commentSaveTimeout);
        }
        window.commentSaveTimeout = setTimeout(async () => {
            await saveVoteToSupabase(projet.projet._id, projet.projet.participationId, voteData);
        }, 500);
    }

    // Filter projects based on search and star filters
    const getFilteredProjects = () => {
        return allProjects.filter(project => {
            // Search filter - use edited names if available
            const projectName = getDisplayProjectName(project);
            const creatorName = getDisplayCreatorName(project);
            const matchesSearch = searchFilter === "" ||
                projectName.toLowerCase().includes(searchFilter.toLowerCase()) ||
                creatorName.toLowerCase().includes(searchFilter.toLowerCase());

            // Star filter
            const projectRating = ratings.find(rating =>
                rating.participationId === project.projet.participationId &&
                rating.projetId === project.projet._id
            );

            let matchesStarFilter = true;
            if (starFilter === "1") {
                matchesStarFilter = projectRating?.stars === 1;
            } else if (starFilter === "2") {
                matchesStarFilter = projectRating?.stars === 2;
            } else if (starFilter === "3") {
                matchesStarFilter = projectRating?.stars === 3;
            } else if (starFilter === "unrated") {
                matchesStarFilter = !projectRating?.stars || projectRating.stars === 0;
            }

            return matchesSearch && matchesStarFilter;
        });
    };

    // Calculate statistics
    const totalProjects = allProjects.length;
    const votedProjects = allProjects.filter(project =>
        ratings.find(rating => rating.participationId === project.projet.participationId && rating.projetId === project.projet._id)?.stars > 0
    ).length;
    const totalFavorites = ratings.filter(rating => rating.Coup_de_coeur === "checked").length;
    const progressPercentage = totalProjects > 0 ? Math.round((votedProjects / totalProjects) * 100) : 0;

    return (
        <Container>
            <Header>
                <H2>{event.title_fr} - {i18n.language === "fr" ? "Mention Contract" : "Contract Mention"}</H2>
                <p className="subtitle">
                    {i18n.language === "fr"
                        ? "Votez pour les meilleurs projets parmi les lauréats du FD100 2025"
                        : "Vote for the best projects among Le FD100 2025 winners"}
                </p>
            </Header>

            <StatsContainer>
                <div className="stats-item">
                    <div className="number">{totalProjects}</div>
                    <div className="label">{i18n.language === "fr" ? "projets" : "projects"}</div>
                </div>

                <div className="progress-container">
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${progressPercentage}%` }} />
                    </div>
                    <div className="progress-label">
                        {i18n.language === "fr"
                            ? `${votedProjects}/${totalProjects} votés`
                            : `${votedProjects}/${totalProjects} voted`
                        }
                    </div>
                </div>

                <div className="stats-item">
                    <div className="number">{totalFavorites}</div>
                    <div className="label">{i18n.language === "fr" ? "coups de cœur" : "favorites"}</div>
                </div>
            </StatsContainer>

            <FilterContainer>
                <div className="filter-group">
                    <span className="filter-label">
                        {i18n.language === "fr" ? "Recherche :" : "Search:"}
                    </span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder={i18n.language === "fr" ? "Rechercher un projet ou designer..." : "Search project or designer..."}
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <span className="filter-label">
                        {i18n.language === "fr" ? "Filtrer :" : "Filter:"}
                    </span>
                    <div className="star-filter">
                        <button
                            className={starFilter === "all" ? "active" : ""}
                            onClick={() => setStarFilter("all")}
                        >
                            {i18n.language === "fr" ? "Tout" : "All"}
                        </button>
                        <button
                            className={starFilter === "unrated" ? "active" : ""}
                            onClick={() => setStarFilter("unrated")}
                        >
                            {i18n.language === "fr" ? "Non noté" : "Unrated"}
                        </button>
                        <button
                            className={starFilter === "1" ? "active" : ""}
                            onClick={() => setStarFilter("1")}
                        >
                            1★
                        </button>
                        <button
                            className={starFilter === "2" ? "active" : ""}
                            onClick={() => setStarFilter("2")}
                        >
                            2★
                        </button>
                        <button
                            className={starFilter === "3" ? "active" : ""}
                            onClick={() => setStarFilter("3")}
                        >
                            3★
                        </button>
                    </div>
                </div>
            </FilterContainer>

            <ProjectsSection>
                {getFilteredProjects().sort((a, b) => {
                    const displayNameA = getDisplayCreatorName(a).toLowerCase();
                    const displayNameB = getDisplayCreatorName(b).toLowerCase();
                    return displayNameA.localeCompare(displayNameB);
                }).map((project, j) => {
                    const projectRating = ratings.find(rating =>
                        rating.participationId === project.projet.participationId &&
                        rating.projetId === project.projet._id
                    );
                    const isVoted = projectRating?.stars > 0;

                    // Get first image from project medias (same as FD100FinalSelection.js)
                    const getProjectThumbnail = () => {
                        if (project.projet.medias && project.projet.medias.length > 0) {
                            const firstMedia = project.projet.medias[0];
                            if (firstMedia.type && firstMedia.type.includes('image')) {
                                return process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + firstMedia.path;
                            } else if (firstMedia.type && firstMedia.type.includes('pdf')) {
                                const filename = firstMedia.path.split('/').pop();
                                const pdfPreviewPath = firstMedia.path.replace(filename, "firstpage_" + filename).replace('.pdf', '.png');
                                return process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + pdfPreviewPath;
                            }
                        }
                        return null;
                    };
                    const thumbnailUrl = getProjectThumbnail();

                    return (
                        <RatingLine key={j} className={isVoted ? 'voted' : ''}>
                            <div className="project-content">
                                <ProjectThumbnail>
                                    <a
                                        href={"/admin/events/fdficheprojet?email=" + project.user.user.email + "&pid=" + project.user.participation._id + "&projid=" + project.projet._id + "&mode=jury"}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {thumbnailUrl ? (
                                            <img src={thumbnailUrl} alt={getDisplayProjectName(project)} />
                                        ) : (
                                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '10px' }}>
                                                No image
                                            </div>
                                        )}
                                    </a>
                                </ProjectThumbnail>
                                <div className='projinfo'>
                                    <div className="project-name">
                                        <a
                                            href={"/admin/events/fdficheprojet?email=" + project.user.user.email + "&pid=" + project.user.participation._id + "&projid=" + project.projet._id + "&mode=jury"}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <BoldBlue style={{ fontSize: "16px" }}>
                                                {getDisplayProjectName(project)}
                                            </BoldBlue>
                                        </a>
                                        {project.projet.fabrication_fr === "oui" && <FabFrIndicator />}
                                    </div>
                                    <a
                                        href={getEventDesignerProfileLink(project.projet, project.user)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="designer-name"
                                    >
                                        {getDisplayCreatorName(project)}
                                    </a>
                                </div>
                            </div>

                            <div className='stars'>
                                <span
                                    onClick={() => projectRating?.stars === 1 ? setStar(project, 0) : setStar(project, 1)}
                                >
                                    {projectRating?.stars >= 1 ? "★" : "☆"}
                                </span>
                                <span onClick={() => setStar(project, 2)}>
                                    {projectRating?.stars >= 2 ? "★" : "☆"}
                                </span>
                                <span onClick={() => setStar(project, 3)}>
                                    {projectRating?.stars >= 3 ? "★" : "☆"}
                                </span>
                            </div>

                            <label>
                                <span>{i18n.language === "fr" ? "Coup de cœur" : "Favorite"}</span>
                                <input
                                    onChange={(e) => setCheckbox(project, e)}
                                    type="checkbox"
                                    name="Coup_de_coeur"
                                    checked={projectRating?.Coup_de_coeur === "checked"}
                                />
                            </label>

                            <input
                                className="comment-input"
                                onChange={(e) => setCommentaire(project, e)}
                                type="text"
                                placeholder={i18n.language === "fr" ? "Votre commentaire..." : "Your comment..."}
                                value={projectRating?.commentaire || ""}
                            />
                        </RatingLine>
                    );
                })}

                {getFilteredProjects().length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        {i18n.language === "fr"
                            ? "Aucun projet ne correspond à vos critères de recherche."
                            : "No projects match your search criteria."}
                    </div>
                )}
            </ProjectsSection>

            <SaveNotification status={dataStatus} show={showSaveNotification}>
                {dataStatus === "loading" && (
                    <>
                        <div className="spinner"></div>
                        {i18n.language === "fr" ? "Enregistrement..." : "Saving..."}
                    </>
                )}
                {dataStatus === "saved" && (
                    <>
                        <span className="checkmark">✓</span>
                        {i18n.language === "fr" ? "Sauvegarde réussie" : "Saved successfully"}
                    </>
                )}
            </SaveNotification>
        </Container>
    );
};

export default MentionContractJuryProfile;
